import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";

import { Caret } from "commons";
import { isPrintMode } from "commons/src/QueryParameter";

export const ACCOUNT_DETAIL_TYPES = {
    primary: "primary",
    secondary: "secondary",
    single: "single"
};

const headerSizes = {
    TITLE_BIG: "1.5rem",
    TITLE_NORMAL: "1.125rem",
    SUBTITLE_BIG: "1.125rem",
    SUBTITLE_NORMAL: "1rem",
    SUBTITLE_SMALL: "0.875rem"
};

const getHeaderFontSize = headerSize => ({
    desktopTitle: headerSize === "big" ? headerSizes.TITLE_BIG : headerSizes.TITLE_NORMAL,
    mobileTitle: headerSizes.TITLE_NORMAL,
    desktopSubtitle: headerSize === "big" ? headerSizes.SUBTITLE_BIG : headerSizes.SUBTITLE_NORMAL,
    mobileSubtitle: headerSizes.SUBTITLE_SMALL
});

const getLayout = type => (type === "single" ? "center" : "space-between");

export function AccountDetail({
    type = "secondary",
    content = null,
    heroContent = "",
    heroLabel = "",
    action = null,
    links = [],
    title = "",
    subtitle = "",
    headerSize = "medium",
    alignToTop,
    marginBottom,
    pageBreakInside
}) {
    const [collapsed, setCollapsed] = useState(type === "secondary");

    const size = getHeaderFontSize(headerSize);

    const isCollapsible = type !== "primary";

    const handleHeaderClick = () => {
        if (type === "primary") {
            return null;
        }

        setCollapsed(v => !v);
    };

    return (
        <MainContainer
            data-testid="detail-container"
            marginBottom={marginBottom}
            pageBreakInside={pageBreakInside}
        >
            <HeaderContainer
                onClick={handleHeaderClick}
                type={type}
                data-testid="detail-header-container"
            >
                <HeaderContent>
                    <TitleContainer type={type}>
                        <Title data-testid="detail-title" size={size}>
                            {title}
                            {isCollapsible && !isPrintMode() && (
                                <Caret
                                    id="caret-icon"
                                    fontSize="large"
                                    direction={collapsed ? "down" : "up"}
                                />
                            )}
                            {type !== "primary" && (
                                <ShowMore id="expand-action">
                                    Show {collapsed ? "more" : "less"}
                                </ShowMore>
                            )}
                        </Title>
                        <Subtitle data-testid="detail-subtitle" size={size}>
                            {subtitle}
                        </Subtitle>
                    </TitleContainer>
                    <InlineHeroContent type={type} size={size} collapsed={collapsed}>
                        {heroLabel} {heroContent}
                    </InlineHeroContent>
                    <ActionsContainer data-testid="detail-actions-container" type={type}>
                        {links.length > 0 && (
                            <TopLinksContainer type={type} data-testid="detail-top-links-container">
                                {[...links]}
                            </TopLinksContainer>
                        )}
                        {action}
                    </ActionsContainer>
                </HeaderContent>
                <TopHeroContent type={type} data-testid="detail-top-hero-content">
                    {heroContent}
                </TopHeroContent>
            </HeaderContainer>

            {(!collapsed || isPrintMode()) && (
                <ContentContainer type={type} data-testid="detail-content-container">
                    <HeroContainer
                        alignToTop={alignToTop}
                        type={type}
                        data-testid="detail-hero-container"
                    >
                        <HeroLabel data-testid="detail-hero-label">{heroLabel}</HeroLabel>
                        <HeroContent data-testid="detail-hero-content">{heroContent}</HeroContent>
                    </HeroContainer>
                    <Content type={type} data-testid="detail-content">
                        {content}
                        {links.length > 0 && type === "primary" && (
                            <BottomLinksContainer
                                type={type}
                                data-testid="detail-bottom-links-container"
                            >
                                {[...links]}
                            </BottomLinksContainer>
                        )}
                    </Content>
                </ContentContainer>
            )}
        </MainContainer>
    );
}

const MainContainer = styled.div`
    width: 100%;
    background-color: white;
    margin-bottom: ${props => props.marginBottom || "2rem"};
    page-break-inside: ${props => props.pageBreakInside || "auto"};

    @media print {
        margin-bottom: 0;
    }
`;

const slideIn = keyframes`
    0% {
        transform: translateX(-150px);
        opacity: 0;
    }

    50% {
        transform: translate(-40px);
        opacity: 0;
    }

    75% {
        transform: translate(-10px);
        opacity: 0.4;
    }

    100% {
        transform: translateX(0);
        opacity: 0.8;
    }
`;

const scaleUp = keyframes`
    0% {
        transform: scale(1);
    }

    100% {
        transform: scale(1.2);
    }
`;

const HeaderContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${props => props.theme.primary};
    padding-bottom: ${props => (props.type === "primary" ? "0.5rem" : "0")};

    @media print {
        padding-bottom: 0;
    }

    @media (min-width: ${props => props.theme.minWidth}) {
        :hover {
            #expand-action {
                animation: ${slideIn} 0.2s linear forwards;

                @media (prefers-reduced-motion) {
                    animation: none;
                }
            }

            #caret-icon {
                animation: ${scaleUp} 0.4s cubic-bezier(0.18, 1.04, 0.82, 0.83) forwards;

                @media (prefers-reduced-motion) {
                    animation: none;
                }
            }
        }
    }
`;

const ShowMore = styled.span`
    width: 150px;
    font-size: 14px;
    font-family: Helvetica, Arial, sans-serif;
    opacity: 0;
    padding: 0;

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: none;
    }
`;

const HeaderContent = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    font-family: vag-rounded-light, Helvetica, Arial, sans-serif;
    color: ${props => props.theme.primary};
    cursor: ${props => (props.type === "primary" ? "auto" : "pointer")};
`;

const Title = styled.div`
    display: flex;
    align-items: center;
    font-size: ${props => props.size.desktopTitle};

    @media (max-width: ${props => props.theme.maxWidth}) {
        font-size: ${props => props.size.mobileTitle};
    }
`;

const Subtitle = styled(Title)`
    font-size: ${props => props.size.desktopSubtitle};

    @media (max-width: ${props => props.theme.maxWidth}) {
        font-size: ${props => props.size.mobileSubtitle};
    }
`;

const TopLinksContainer = styled.div`
    display: ${props => (props.type === "primary" ? "flex" : "none")};

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: none;
    }
`;

const BottomLinksContainer = styled.div`
    display: ${props => (props.type === "primary" ? "none" : "flex")};
    justify-content: flex-end;
    margin-top: 1rem;

    @media (max-width: 600px) {
        display: ${props => (props.disabled ? "none" : "flex")};
        justify-content: space-evenly;
    }

    @media (min-width: 601px) and (max-width: ${props => props.theme.maxWidth}) {
        display: ${props => (props.disabled ? "none" : "flex")};
        justify-content: flex-end;
    }

    @media print {
        display: none;
    }
`;

const ActionsContainer = styled.div`
    display: ${props => (props.type === "primary" ? "flex" : "none")};
    align-items: center;

    @media print {
        display: none;
    }
`;

const HeroLabel = styled.span`
    font-size: 1.5rem;
`;

const HeroContent = styled.span`
    font-size: 1.5rem;
`;

const InlineHeroContent = styled.span`
    display: ${props => (props.type === "primary" || !props.collapsed ? "none" : "flex")};
    font-size: 0.875rem;
    color: ${props => props.theme.primary};
    justify-self: end;
    align-items: center;

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: none;
    }

    @media print {
        display: none;
    }
`;

const TopHeroContent = styled(HeroContent)`
    color: ${props => props.theme.primary};
    display: none;
    font-weight: 300;

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: ${props => (props.type === "primary" ? "block" : "none")};
        margin-top: 1.5rem;
    }

    @media print {
        display: none;
    }
`;

const HeroContainer = styled.div`
    width: 100%;
    display: ${props => (props.type === "single" ? "none" : "flex")};
    flex-direction: column;
    justify-content: ${props => (props.alignToTop ? "flex-start" : "center")};
    align-items: center;
    color: ${props => props.theme.primary};
    padding-top: ${props => (props.alignToTop ? "5rem" : "0")};
    font-weight: 300;

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: none;
    }

    @media print {
        display: ${props => (props.type === "single" ? "none" : "flex")};
    }
`;

const ContentContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: ${props => getLayout(props.type)};
`;

const Content = styled.div`
    width: ${props => (props.type === "single" ? "70%" : "100%")};

    @media (max-width: ${props => props.theme.maxWidth}) {
        width: 100%;
    }

    @media print {
        width: ${props => (props.type === "single" ? "70%" : "100%")};
    }
`;

AccountDetail.propTypes = {
    type: PropTypes.string,
    content: PropTypes.element,
    heroContent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    heroLabel: PropTypes.string,
    action: PropTypes.element,
    links: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    headerSize: PropTypes.string,
    alignToTop: PropTypes.bool,
    marginBottom: PropTypes.string,
    pageBreakInside: PropTypes.string
};
